import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APIService, KPI } from '../api';

import { ErrorService } from './error.service';
import { HistoryService } from './history.service';
import { IdMap, ScopeService } from './scope.service';

/**
 * Used to get and to format an investment history
 */
@Injectable()
export class AssetHistoryService extends HistoryService {

  private zones: IdMap<KPI.Zone>;
  private ownershipTypes: IdMap<KPI.OwnershipType>;
  private assetTypes: IdMap<KPI.AssetType>;

  constructor(
    protected errorService: ErrorService,
    protected translateService: TranslateService,
    private api: APIService,
    private scope: ScopeService,
  ) {
    super(errorService, translateService);


    this.translationMap = {
      'zone': _('asset.zone'),
      'zone_details': _('asset.zone_details'),
      'asset_type': _('asset.asset_type'),
      'duration_deviation': _('asset.duration_deviation'),
      'installation_year': _('asset.installation_year'),
      'quantity': _('asset.quantity'),
      'ownership_type': _('asset.ownership_type'),
      'comments': _('asset.comments'),
      'maintenance_code': _('asset.maintenance_code'),
      'label': _('asset.label'),
      'qrcode': _('asset.qrcode'),
      'gross_value': _('asset.gross_value'),
      'net_value': _('asset.net_value'),
      'deviation_reason': _('asset.deviation_reason'),
      'barcode': _('asset.barcode'),
    };


    this.undisplayedObject = [
      'investment',
      'investmentsponsorship',
      'investmentslice',
    ];

    this.undisplayedFields = [
      'id',
      'building_id',
      'zone_id',
      'asset_type_id',
      'ownership_type_id',
      'deviation_reason_id',
      'is_deleted',
      'last_change',
      'suggested_lifetime',
      'suggested_lifetime_year',
      'first_audit_year',
      'first_audit_note',
      'local_id',
      'status',
    ];
  }

  /**
   * Load the history for an asset
   *
   * @param asset the asset that we wish to recover its history
   */
  public loadHistoryForAsset(assetId: number): Observable<KPI.History> {
    return combineLatest(
      this.api.assets_asset_history(assetId),
      this.scope.zones$,
      this.scope.ownershipTypes$,
      this.scope.assetTypes$,
    ).pipe(
      map(([
        history,
        zones,
        ownershipTypes,
        assetTypes,
      ]) => {
        this.zones = zones;
        this.ownershipTypes = ownershipTypes;
        this.assetTypes = assetTypes;
        return this.cleanHistory(history);
      },
      ),
    );
  }


  /**
   * If the change is not clear for the user we recover some information that we will display
   *
   * @param change
   */
  protected manageSpecialField(change: KPI.ChangeItem): KPI.ChangeItem {
    const newChange: KPI.ChangeItem = change;
    switch (change.field_name) {
      case 'zone_id':
        newChange.field_name = 'zone';
        newChange.new = this.getName(this.zones[change.new.toString()]);
        if (change.old) {
          newChange.old = this.getName(this.zones[change.old.toString()]);
        }
        break;

      case 'asset_type_id':
        newChange.field_name = 'asset_type';
        newChange.new = this.getName(this.assetTypes[change.new.toString()]);
        if (change.old) {
          newChange.old = this.getName(this.assetTypes[change.old.toString()]);
        }
        break;

      case 'ownership_type_id':
        newChange.field_name = 'ownership_type';
        if (change.new) {
          newChange.new = this.getName(this.ownershipTypes[change.new.toString()]);
        }
        if (change.old) {
          newChange.old = this.getName(this.ownershipTypes[change.old.toString()]);
        }
        break;

      default:
        if (typeof newChange.new === 'boolean') {
          newChange.new = this.boolean2String(<boolean>change.new);
        }
        if (typeof newChange.old === 'boolean') {
          newChange.old = this.boolean2String(<boolean>change.old);
        }
        break;
    }

    return newChange;
  }
}
